import { timezone } from "constants/date";
import { NotTaggedFilterId } from "constants/filters";
import { trimObject } from "helpers/object";
import { TicketStatus } from "interfaces/entities/ticket";

import { IRequestResult } from "../api-client";
import { BaseApi } from "../base";
import { mapDateRange } from "../helpers";

import {
  IHandlingTimeByStatus,
  INewTickets,
  IResponseTime,
  ITicketRatingsPerEntity,
  ITicketsStatusStats,
} from "./interfaces";
import { ReportExportType } from "constants/reports/report-type";

function getEndpoint(prefix: string, distribution: string, heatmap = false): string {
  if (distribution === "hour" && !heatmap) {
    return `${prefix}24`;
  }
  return prefix;
}

function getStep(distribution: string, heatmap = false): { "range.step": string } | undefined {
  if (distribution === "hour" && !heatmap) {
    return undefined;
  }

  return {
    "range.step": heatmap ? "hour" : distribution,
  };
}

function getTag(tagId: string[] | null): { tagIDs: string[] } | { hasTags: boolean } | null {
  if (!tagId) {
    return null;
  }

  const notTaggedIndex = tagId.indexOf(NotTaggedFilterId);

  if (notTaggedIndex !== -1) {
    const allButNotTagged = [...tagId.slice(0, notTaggedIndex), ...tagId.slice(notTaggedIndex + 1)];

    return {
      ...(allButNotTagged.length > 0 ? { tagIDs: allButNotTagged } : {}),
      hasTags: false,
    };
  }

  return {
    tagIDs: tagId,
  };
}

export default class ReportApi extends BaseApi {
  protected readonly prefix = "reports";

  fetchNewTickets = ({
    dateFrom = null,
    dateTo = null,
    distribution = "day",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    heatmap = false,
    priority = null,
    priorityEq = null,
    language = null,
  }): Promise<IRequestResult<INewTickets>> =>
    this.get(
      getEndpoint("newTickets", distribution, heatmap),
      trimObject({
        "range.timezone": timezone,
        ...getStep(distribution, heatmap),
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(language ? { language } : null),
        tagged,
        spam: false,
      })
    );

  fetchTicketSources = ({
    dateFrom = null,
    dateTo = null,
    distribution = "day",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    language = null,
  }): Promise<IRequestResult<IHandlingTimeByStatus>> =>
    this.get(
      getEndpoint("ticketDetailedSources", distribution),
      trimObject({
        "range.timezone": timezone,
        ...getStep(distribution),
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(language ? { language } : null),
        tagged,
        spam: false,
      })
    );

  fetchTicketsWithStatus = ({
    dateFrom = null,
    dateTo = null,
    distribution = "day",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    status = TicketStatus.Solved,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<ITicketsStatusStats>> =>
    this.get(
      getEndpoint("status", distribution),
      trimObject({
        "range.timezone": timezone,
        ...getStep(distribution),
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        status,
        spam: false,
      })
    );
  fetchTicketsWithStatusPerTeam = ({
    dateFrom = null,
    dateTo = null,
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    status = TicketStatus.Solved,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<ITicketsStatusStats>> =>
    this.get(
      getEndpoint("statusPerTeam", "day"),
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        status,
        spam: false,
      })
    );
  fetchTicketsWithStatusPerAgent = ({
    dateFrom = null,
    dateTo = null,
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    status = TicketStatus.Solved,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<ITicketsStatusStats>> =>
    this.get(
      getEndpoint("statusPerAgent", "day"),
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        status,
        spam: false,
      })
    );
  fetchSolvedTickets = (
    params: Omit<Parameters<typeof this.fetchTicketsWithStatus>[0], "status">
  ): Promise<IRequestResult<ITicketsStatusStats>> =>
    this.fetchTicketsWithStatus({
      ...params,
      status: TicketStatus.Solved,
    });
  fetchClosedTickets = (
    params: Omit<Parameters<typeof this.fetchTicketsWithStatus>[0], "status">
  ): Promise<IRequestResult<ITicketsStatusStats>> =>
    this.fetchTicketsWithStatus({
      ...params,
      status: TicketStatus.Closed,
    });
  fetchSolvedTicketsPerAgent = (
    params: Omit<Parameters<typeof this.fetchTicketsWithStatus>[0], "status">
  ): Promise<IRequestResult<ITicketsStatusStats>> =>
    this.fetchTicketsWithStatusPerAgent({
      ...params,
      status: TicketStatus.Solved,
    });
  fetchClosedTicketsPerAgent = (
    params: Omit<Parameters<typeof this.fetchTicketsWithStatus>[0], "status">
  ): Promise<IRequestResult<ITicketsStatusStats>> =>
    this.fetchTicketsWithStatusPerAgent({
      ...params,
      status: TicketStatus.Closed,
    });
  fetchSolvedTicketsPerTeam = (
    params: Omit<Parameters<typeof this.fetchTicketsWithStatus>[0], "status">
  ): Promise<IRequestResult<ITicketsStatusStats>> =>
    this.fetchTicketsWithStatusPerTeam({
      ...params,
      status: TicketStatus.Solved,
    });
  fetchClosedTicketsPerTeam = (
    params: Omit<Parameters<typeof this.fetchTicketsWithStatus>[0], "status">
  ): Promise<IRequestResult<ITicketsStatusStats>> =>
    this.fetchTicketsWithStatusPerTeam({
      ...params,
      status: TicketStatus.Closed,
    });

  fetchTicketsFirstResponseTime = ({
    dateFrom = null,
    dateTo = null,
    distribution = "day",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<IResponseTime>> =>
    this.get(
      getEndpoint("responseTime", distribution),
      trimObject({
        "range.timezone": timezone,
        ...getStep(distribution),
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        status,
        spam: false,
      })
    );
  fetchTicketsFirstResponsePerTeam = ({
    dateFrom = null,
    dateTo = null,
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<IResponseTime>> =>
    this.get(
      getEndpoint("responseTimePerTeam", "day"),
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        status,
        spam: false,
      })
    );
  fetchTicketsFirstResponseTimePerAgent = ({
    dateFrom = null,
    dateTo = null,
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<IResponseTime>> =>
    this.get(
      getEndpoint("responseTimePerAgent", "day"),
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        status,
        spam: false,
      })
    );

  fetchTicketsSatisfaction = ({
    dateFrom = null,
    dateTo = null,
    distribution = "day",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<IHandlingTimeByStatus>> =>
    this.get(
      getEndpoint("rating", distribution),
      trimObject({
        "range.timezone": timezone,
        ...getStep(distribution),
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        spam: false,
      })
    );

  fetchAgentRating = ({
    dateFrom = "",
    dateTo = "",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<ITicketRatingsPerEntity>> =>
    this.get(
      "agentRating",
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
      })
    );

  fetchTicketsSatisfactionPerAgent = ({
    dateFrom = "",
    dateTo = "",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<ITicketRatingsPerEntity>> =>
    this.get(
      "ratingPerAgent",
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
      })
    );
  fetchTicketsSatisfactionPerTeam = ({
    dateFrom = "",
    dateTo = "",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<ITicketRatingsPerEntity>> =>
    this.get(
      "ratingPerTeam",
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
      })
    );

  fetchTicketResolutionTime = ({
    dateFrom = "",
    dateTo = "",
    distribution = "day",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<IHandlingTimeByStatus>> =>
    this.get(
      getEndpoint("resolutionTime", distribution),
      trimObject({
        "range.timezone": timezone,
        ...getStep(distribution),
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        spam: false,
      })
    );
  fetchTicketResolutionTimePerTeam = ({
    dateFrom = "",
    dateTo = "",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<IHandlingTimeByStatus>> =>
    this.get(
      getEndpoint("resolutionTimePerTeam", "day"),
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        spam: false,
      })
    );
  fetchTicketResolutionTimePerAgent = ({
    dateFrom = "",
    dateTo = "",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<IHandlingTimeByStatus>> =>
    this.get(
      getEndpoint("resolutionTimePerAgent", "day"),
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        spam: false,
      })
    );
  fetchHandlingTimeByStatus = ({
    dateFrom = "",
    dateTo = "",
    distribution = "day",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<IHandlingTimeByStatus>> =>
    this.get(
      getEndpoint("statusDuration", distribution),
      trimObject({
        "range.timezone": timezone,
        ...getStep(distribution),
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        spam: false,
      })
    );
  fetchHandlingTimeByStatusTotal = ({
    dateFrom = "",
    dateTo = "",
    distribution = "day",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
    language = null,
  }): Promise<IRequestResult<IHandlingTimeByStatus>> =>
    this.get(
      getEndpoint("statusDurationTotal", distribution),
      trimObject({
        "range.timezone": timezone,
        ...getStep(distribution),
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        ...(language ? { language } : null),
        tagged,
        spam: false,
      })
    );

  fetchHandlingTimePerAgent = ({
    dateFrom = "",
    dateTo = "",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
  }): Promise<IRequestResult<IHandlingTimeByStatus>> =>
    this.get(
      getEndpoint("statusDurationPerAgent", "day"),
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        tagged,
        spam: false,
      })
    );

  fetchHandlingTimePerTeam = ({
    dateFrom = "",
    dateTo = "",
    agent = null,
    group = null,
    tag = null,
    tagged = null,
    priority = null,
    priorityEq = null,
    detailedSource = null,
  }): Promise<IRequestResult<IHandlingTimeByStatus>> =>
    this.get(
      getEndpoint("statusDurationPerTeam", "day"),
      trimObject({
        "range.timezone": timezone,
        ...mapDateRange(dateFrom, dateTo),
        ...getTag(tag),
        ...(agent ? { agentID: agent } : null),
        ...(group ? { teamIDs: group } : null),
        ...(priority ? { priority } : null),
        ...(priorityEq ? { priorityEq } : null),
        ...(detailedSource ? { detailedSource } : null),
        tagged,
        spam: false,
      })
    );

  generateReport = ({
    type,
    dateFrom,
    dateTo,
    recipients,
  }: {
    type: ReportExportType;
    dateFrom: string | Date | null;
    dateTo: string | Date | null;
    recipients: string[];
  }): Promise<IRequestResult<void>> => this.post("", { type: type, range: { timezone, from: dateFrom, to: dateTo }, recipients });
}
